/**
 * WeddingPlannerBlock — Extended internal-only block for the operator to
 * manage the wedding-planner collaboration: anagrafica, operational team,
 * referral toggle, commercial agreement and economic simulation.
 *
 * Replaces the legacy single "Wedding planner" row inside the Vendors tab.
 *
 * Data model (mirrors backend models.WeddingPlanner):
 *   client.wedding_planner = {
 *     agency_name, contact_name, phone, email, instagram, notes,
 *     assistants: [{ name, email, phone }],
 *     referral_enabled: bool,
 *     commercial_agreement: {
 *       type: "none" | "percentage" | "fixed",
 *       percentage_fee, fixed_fee
 *     }
 *   }
 *
 * Read-only data sources (for the simulation card):
 *   client.quote.total — the current customer-facing quote total.
 *
 * Security: ALL fields are internal-only. The public-client endpoint is a
 * whitelist serializer (see backend `/public/client/{token}`) and never
 * surfaces this object. Same applies to PDFs, email templates and shared
 * timeline links.
 */
import React from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Briefcase, Instagram as InstagramIcon, Mail, Phone, Users, Plus, Trash2,
  Handshake, Calculator, Lock, Info,
} from "lucide-react";

const DEFAULT_WP = {
  agency_name: "",
  contact_name: "",
  phone: "",
  email: "",
  instagram: "",
  notes: "",
  assistants: [],
  referral_enabled: false,
  commercial_agreement: { type: "none", percentage_fee: 0, fixed_fee: 0 },
};

const DEFAULT_ASSISTANT = { name: "", email: "", phone: "" };

const fmtEur = (n) => {
  const v = Number(n) || 0;
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
};

// Extract the VAT-net taxable base ("imponibile") from a quote object.
//
// Quote totals follow this pipeline (see backend `_recalc_quote_totals`):
//   subtotal = Σ items
//   after_discount = subtotal * (1 - discount_total/100)   ← THIS is the imponibile
//   total = after_discount * (1 + tax/100)
//
// Fee math (operator requirement Feb 2026): the wedding-planner referral
// fee MUST be computed on the imponibile, never on the VAT-gross total.
// Two safe paths to reach it:
//   1. Prefer `subtotal` minus any percentage discount (matches the exact
//      backend formula — bit-for-bit consistent).
//   2. Fallback to `total / (1 + tax/100)` if subtotal is missing (older
//      docs predating the discount_total field).
// In forfettario / tax==0 cases both paths converge to total.
function deriveTaxableBase(quote) {
  const q = quote || {};
  const total = Number(q.total) || 0;
  const subtotal = Number(q.subtotal) || 0;
  const discountTotal = Number(q.discount_total) || 0;
  const tax = Number(q.tax) || 0;
  if (subtotal > 0) {
    return subtotal * (1 - discountTotal / 100);
  }
  if (total > 0 && tax > 0) {
    return total / (1 + tax / 100);
  }
  return total;
}

// Computes fee + net + suggested gross price based on the agreement.
// All math is anchored on the IMPONIBILE (taxable base, net of VAT), not
// on the VAT-gross total — this matters because the fee is on the
// operator's actual revenue, not on the VAT they will pass through to
// the tax authority.
//
// Returns null when the simulation cannot run (referral OFF or no agreement).
function computeSimulation(quote, agreement) {
  const q = quote || {};
  const total = Number(q.total) || 0;
  const taxable = deriveTaxableBase(q);
  const taxRate = Number(q.tax) || 0;   // %; 0 in forfettario mode
  const vatMultiplier = 1 + taxRate / 100;
  const type = agreement?.type || "none";
  if (type === "none") return null;

  if (type === "percentage") {
    const pct = Math.max(0, Math.min(100, Number(agreement.percentage_fee) || 0));
    const fee = taxable * (pct / 100);
    const net = taxable - fee;
    // Suggested: the imponibile the operator should quote so their NET
    // (after the planner takes the fee) stays equal to today's `taxable`.
    // Formula: suggested_taxable = taxable / (1 - pct/100).
    // We then re-apply VAT to get the customer-facing gross.
    const suggestedTaxable = pct >= 100 ? taxable : taxable / (1 - pct / 100);
    const suggested = suggestedTaxable * vatMultiplier;
    return { fee, net, suggested, suggestedTaxable, taxable, total, taxRate, pct };
  }

  if (type === "fixed") {
    const fee = Math.max(0, Number(agreement.fixed_fee) || 0);
    const net = taxable - fee;
    // Suggested taxable = current taxable + fee → re-apply VAT for the
    // customer-facing price. Net stays equal to today's taxable.
    const suggestedTaxable = taxable + fee;
    const suggested = suggestedTaxable * vatMultiplier;
    return { fee, net, suggested, suggestedTaxable, taxable, total, taxRate };
  }
  return null;
}

export default function WeddingPlannerBlock({ client, patch }) {
  const wp = { ...DEFAULT_WP, ...(client.wedding_planner || {}) };
  // Defensive merge of nested commercial_agreement so partial DB docs don't blow up the form.
  wp.commercial_agreement = { ...DEFAULT_WP.commercial_agreement, ...(wp.commercial_agreement || {}) };
  wp.assistants = Array.isArray(wp.assistants) ? wp.assistants : [];

  // Single source of truth for "save the WP block". All field handlers
  // funnel through here so we never mutate substate without telling the parent.
  const updateWp = (changes) => {
    patch("wedding_planner", { ...wp, ...changes });
  };
  const updateField = (key) => (e) => updateWp({ [key]: e.target.value });
  const updateAgreement = (changes) => {
    updateWp({ commercial_agreement: { ...wp.commercial_agreement, ...changes } });
  };

  // ---- Assistants CRUD ----
  const addAssistant = () => updateWp({ assistants: [...wp.assistants, { ...DEFAULT_ASSISTANT }] });
  const updateAssistant = (i, key, value) => {
    const next = wp.assistants.map((a, idx) => (idx === i ? { ...a, [key]: value } : a));
    updateWp({ assistants: next });
  };
  const removeAssistant = (i) => {
    updateWp({ assistants: wp.assistants.filter((_, idx) => idx !== i) });
  };

  const referralOn = !!wp.referral_enabled;
  const agreement = wp.commercial_agreement;
  const sim = referralOn ? computeSimulation(client?.quote, agreement) : null;

  // Light auto-prefix of "@" for Instagram (matches the legacy VendorRow UX).
  const normaliseInstagram = (raw) => {
    const v = (raw || "").trim();
    if (!v) return "";
    let h = v;
    if (h.startsWith("https://")) {
      try {
        const u = new URL(h);
        h = u.pathname.replace(/^\/+|\/+$/g, "").split("/")[0] || "";
      } catch { /* ignore */ }
    }
    h = h.replace(/^@+/, "");
    return h ? "@" + h : "";
  };

  return (
    <div className="space-y-5" data-testid="wedding-planner-block">
      {/* ---- Card 1: Anagrafica wedding planner ---- */}
      <Card className="rounded-sm bg-white border-border p-5 space-y-5" data-testid="wp-info-card">
        <header className="flex items-center gap-2.5">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-sm bg-primary/10 text-primary">
            <Briefcase className="w-4 h-4" strokeWidth={1.8} />
          </span>
          <div>
            <h3 className="font-serif-heading text-xl text-neutral-900">Informazioni Wedding Planner</h3>
            <p className="text-xs text-neutral-500 mt-0.5">Anagrafica e contatti dell'agenzia</p>
          </div>
        </header>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="label-uppercase">Nome Wedding Planner / Agenzia</Label>
            <Input value={wp.agency_name} onChange={updateField("agency_name")} className="mt-1 rounded-sm" placeholder="Es. Stefania Wedding Events" data-testid="wp-agency-name" />
          </div>
          <div>
            <Label className="label-uppercase">Referente principale</Label>
            <Input value={wp.contact_name} onChange={updateField("contact_name")} className="mt-1 rounded-sm" placeholder="Es. Stefania Rossi" data-testid="wp-contact-name" />
          </div>
          <div>
            <Label className="label-uppercase flex items-center gap-1.5"><Phone className="w-3 h-3" strokeWidth={1.5} /> Telefono</Label>
            <Input type="tel" value={wp.phone} onChange={updateField("phone")} className="mt-1 rounded-sm" placeholder="+39 ..." data-testid="wp-phone" />
          </div>
          <div>
            <Label className="label-uppercase flex items-center gap-1.5"><Mail className="w-3 h-3" strokeWidth={1.5} /> Email</Label>
            <Input type="email" value={wp.email} onChange={updateField("email")} className="mt-1 rounded-sm" placeholder="info@stefaniaevents.it" data-testid="wp-email" />
          </div>
          <div className="md:col-span-2">
            <Label className="label-uppercase flex items-center gap-1.5"><InstagramIcon className="w-3 h-3" strokeWidth={1.5} /> Instagram</Label>
            <Input
              value={wp.instagram}
              onChange={updateField("instagram")}
              onBlur={(e) => {
                const norm = normaliseInstagram(e.target.value);
                if (norm !== wp.instagram) updateWp({ instagram: norm });
              }}
              className="mt-1 rounded-sm"
              placeholder="@stefaniaevents"
              data-testid="wp-instagram"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="label-uppercase">Note operative</Label>
            <Textarea
              value={wp.notes}
              onChange={updateField("notes")}
              className="mt-1 rounded-sm min-h-[90px]"
              placeholder="Orari preferiti, modalità di contatto, richieste operative…"
              data-testid="wp-notes"
            />
          </div>
        </div>
      </Card>

      {/* ---- Card 2: Team operativo ---- */}
      <Card className="rounded-sm bg-white border-border p-5 space-y-5" data-testid="wp-team-card">
        <header className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-sm bg-primary/10 text-primary">
              <Users className="w-4 h-4" strokeWidth={1.8} />
            </span>
            <div>
              <h3 className="font-serif-heading text-xl text-neutral-900">Team operativo</h3>
              <p className="text-xs text-neutral-500 mt-0.5">Assistenti e collaboratori che lavoreranno sul matrimonio</p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="rounded-sm" onClick={addAssistant} data-testid="wp-add-assistant">
            <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Aggiungi assistente
          </Button>
        </header>

        {wp.assistants.length === 0 ? (
          <p className="text-sm text-neutral-400 italic" data-testid="wp-assistants-empty">
            Nessun assistente aggiunto. Clicchi "Aggiungi assistente" per inserire il primo.
          </p>
        ) : (
          <div className="space-y-3">
            {wp.assistants.map((a, i) => (
              <div
                key={i}
                className="grid md:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end rounded-sm border border-border p-3"
                style={{ backgroundColor: "#FFFDFA" }}
                data-testid={`wp-assistant-row-${i}`}
              >
                <div>
                  <Label className="label-uppercase">Nome</Label>
                  <Input value={a.name || ""} onChange={(e) => updateAssistant(i, "name", e.target.value)} className="mt-1 rounded-sm" placeholder="Es. Giulia Bianchi" data-testid={`wp-assistant-${i}-name`} />
                </div>
                <div>
                  <Label className="label-uppercase">Email</Label>
                  <Input type="email" value={a.email || ""} onChange={(e) => updateAssistant(i, "email", e.target.value)} className="mt-1 rounded-sm" placeholder="giulia@studio.it" data-testid={`wp-assistant-${i}-email`} />
                </div>
                <div>
                  <Label className="label-uppercase">Telefono</Label>
                  <Input type="tel" value={a.phone || ""} onChange={(e) => updateAssistant(i, "phone", e.target.value)} className="mt-1 rounded-sm" placeholder="+39 ..." data-testid={`wp-assistant-${i}-phone`} />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-sm text-neutral-400 hover:text-red-600 mb-0.5"
                  onClick={() => removeAssistant(i)}
                  data-testid={`wp-assistant-${i}-remove`}
                  aria-label="Rimuovi assistente"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ---- Card 3: Referral toggle ---- */}
      <Card className="rounded-sm bg-white border-border p-5 space-y-3" data-testid="wp-referral-card">
        <header className="flex items-center gap-2.5">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-sm bg-primary/10 text-primary">
            <Handshake className="w-4 h-4" strokeWidth={1.8} />
          </span>
          <div>
            <h3 className="font-serif-heading text-xl text-neutral-900">Collaborazione e referral</h3>
            <p className="text-xs text-neutral-500 mt-0.5">Conferma se la cliente è arrivata realmente tramite questo wedding planner</p>
          </div>
        </header>

        <label className="flex items-center justify-between gap-4 rounded-sm border border-border px-4 py-3 cursor-pointer hover:bg-neutral-50 transition-colors">
          <div className="space-y-0.5">
            <div className="text-sm font-medium text-neutral-800">Cliente arrivata tramite questo Wedding Planner</div>
            <div className="text-xs text-neutral-500">Attiva fee, simulazione economica e tracciamento KPI futuri.</div>
          </div>
          <Switch
            checked={referralOn}
            onCheckedChange={(checked) => updateWp({ referral_enabled: !!checked })}
            data-testid="wp-referral-toggle"
          />
        </label>

        {!referralOn && (
          <div className="text-xs text-neutral-500 italic flex items-start gap-1.5 px-1">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Fee e simulazione economica disponibili solo quando il referral è attivo.</span>
          </div>
        )}
      </Card>

      {/* ---- Card 4: Accordi commerciali (only when referral ON) ---- */}
      {referralOn && (
        <Card className="rounded-sm bg-white border-border p-5 space-y-5" data-testid="wp-agreement-card">
          <header className="flex items-center gap-2.5">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-sm bg-primary/10 text-primary">
              <Calculator className="w-4 h-4" strokeWidth={1.8} />
            </span>
            <div>
              <h3 className="font-serif-heading text-xl text-neutral-900">Accordi commerciali</h3>
              <p className="text-xs text-neutral-500 mt-0.5">Tipo di compenso pattuito con il wedding planner</p>
            </div>
          </header>

          <div className="space-y-2">
            <Label className="label-uppercase">Tipo di accordo</Label>
            <div className="grid md:grid-cols-3 gap-2">
              {[
                { value: "none",       label: "Nessun accordo" },
                { value: "percentage", label: "Percentuale sul servizio" },
                { value: "fixed",      label: "Importo fisso per sposa" },
              ].map((opt) => {
                const selected = (agreement.type || "none") === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => updateAgreement({ type: opt.value })}
                    className={`text-left rounded-sm border px-3 py-2.5 text-sm transition-colors ${
                      selected
                        ? "border-primary bg-primary/5 text-neutral-900"
                        : "border-border bg-white text-neutral-700 hover:border-primary/40"
                    }`}
                    data-testid={`wp-agreement-${opt.value}`}
                  >
                    <span className={`inline-block w-3 h-3 rounded-full border mr-2 align-middle ${
                      selected ? "border-primary bg-primary" : "border-neutral-300"
                    }`} />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {agreement.type === "percentage" && (
            <div className="md:max-w-xs">
              <Label className="label-uppercase">Percentuale fee</Label>
              <div className="relative mt-1">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={agreement.percentage_fee ?? ""}
                  onChange={(e) => updateAgreement({ percentage_fee: e.target.value === "" ? 0 : Number(e.target.value) })}
                  className="rounded-sm pr-10"
                  placeholder="20"
                  data-testid="wp-percentage-fee"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-neutral-500">%</span>
              </div>
            </div>
          )}

          {agreement.type === "fixed" && (
            <div className="md:max-w-xs">
              <Label className="label-uppercase">Fee fissa</Label>
              <div className="relative mt-1">
                <Input
                  type="number"
                  min={0}
                  step={10}
                  value={agreement.fixed_fee ?? ""}
                  onChange={(e) => updateAgreement({ fixed_fee: e.target.value === "" ? 0 : Number(e.target.value) })}
                  className="rounded-sm pr-10"
                  placeholder="150"
                  data-testid="wp-fixed-fee"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-neutral-500">€</span>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* ---- Card 5: Simulazione economica (only when referral ON and agreement != none) ---- */}
      {referralOn && sim && (
        <Card className="rounded-sm bg-white border-border p-5 space-y-4" data-testid="wp-simulation-card">
          <header className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2.5">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-sm bg-primary/10 text-primary">
                <Calculator className="w-4 h-4" strokeWidth={1.8} />
              </span>
              <div>
                <h3 className="font-serif-heading text-xl text-neutral-900">Simulazione economica</h3>
                <p className="text-xs text-neutral-500 mt-0.5">
                  Calcolo automatico basato sul totale del preventivo. Solo simulazione — nessuna modifica al preventivo cliente.
                </p>
              </div>
            </div>
            <span
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full whitespace-nowrap"
              style={{ backgroundColor: "rgba(200,169,126,0.12)", color: "#7B6A45", border: "1px solid rgba(200,169,126,0.4)" }}
            >
              <Lock className="w-3 h-3" strokeWidth={1.5} /> Solo uso interno
            </span>
          </header>

          <div className="grid sm:grid-cols-2 gap-3">
            <SimRow label="Totale Preventivo Cliente (IVA inclusa)" value={fmtEur(sim.total)} testid="wp-sim-total" />
            <SimRow
              label={sim.taxRate > 0 ? `Imponibile (netto IVA ${sim.taxRate}%)` : "Imponibile"}
              value={fmtEur(sim.taxable)}
              testid="wp-sim-taxable"
              tone="accent"
            />
            <SimRow
              label={agreement.type === "percentage" ? `Fee Wedding Planner (${sim.pct}%)` : "Fee Wedding Planner"}
              value={fmtEur(sim.fee)}
              testid="wp-sim-fee"
              tone="warn"
            />
            <SimRow
              label="Ricavo netto stimato"
              value={fmtEur(sim.net)}
              testid="wp-sim-net"
              tone={sim.net >= 0 ? "ok" : "warn"}
            />
            <div className="sm:col-span-2">
              <SimRow
                label="Prezzo consigliato per mantenere il margine (IVA inclusa)"
                value={fmtEur(sim.suggested)}
                testid="wp-sim-suggested"
                tone="accent"
              />
            </div>
          </div>

          <div className="border-t border-border pt-3 space-y-2">
            <p className="text-xs text-neutral-500 italic flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                La fee del Wedding Planner viene sempre calcolata sull'<strong>imponibile</strong>
                {sim.taxRate > 0 ? ` (totale al netto dell'IVA ${sim.taxRate}%)` : ""}, mai sul totale IVA inclusa.
              </span>
            </p>
            <p className="text-xs text-neutral-500 leading-relaxed">
              {agreement.type === "percentage" ? (
                <>
                  Per mantenere un ricavo netto di <strong>{fmtEur(sim.taxable)}</strong> nonostante la fee del {sim.pct}%,
                  dovrebbe quotare l'imponibile a <strong>{fmtEur(sim.suggestedTaxable)}</strong>
                  {sim.taxRate > 0 ? <> e applicare l'IVA per un totale cliente di <strong>{fmtEur(sim.suggested)}</strong>.</> : <>.</>}
                </>
              ) : (
                <>
                  Per mantenere un ricavo netto di <strong>{fmtEur(sim.taxable)}</strong> nonostante la fee fissa di <strong>{fmtEur(sim.fee)}</strong>,
                  dovrebbe quotare l'imponibile a <strong>{fmtEur(sim.suggestedTaxable)}</strong>
                  {sim.taxRate > 0 ? <> e applicare l'IVA per un totale cliente di <strong>{fmtEur(sim.suggested)}</strong>.</> : <>.</>}
                </>
              )}
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}

function SimRow({ label, value, testid, tone }) {
  // Visual tone variants: ok=green, warn=rose, accent=champagne (default neutral).
  const colorMap = {
    ok:     { text: "text-emerald-700", bg: "bg-emerald-50",  border: "border-emerald-200" },
    warn:   { text: "text-rose-700",    bg: "bg-rose-50",     border: "border-rose-200" },
    accent: { text: "text-[#7B6A45]",   bg: "bg-[#FFFAEF]",   border: "border-[#E8D9B7]" },
    none:   { text: "text-neutral-800", bg: "bg-neutral-50",  border: "border-neutral-200" },
  };
  const c = colorMap[tone] || colorMap.none;
  return (
    <div className={`rounded-sm border ${c.border} ${c.bg} px-4 py-3 flex items-baseline justify-between gap-3`} data-testid={testid}>
      <span className="text-xs text-neutral-600">{label}</span>
      <span className={`text-lg font-semibold tracking-tight ${c.text}`}>{value}</span>
    </div>
  );
}
